import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { GetAdminProjectResponse } from 'src/project/types/projects';
import { getMultipleProjectFiles2 } from 'src/file/api';
import { SitelifeFile } from 'src/file/types';
import { projectMatchesFileTypeFilter } from '../utils/fileTypeUtils';
import {
  chunkArray,
  createBatchQueryKey,
  createProjectFilesMap,
} from '../utils/batchUtils';

// Configuration constants
const BATCH_SIZE = 100;
const STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Custom hook that fetches files for multiple projects and applies file type filtering
 * Uses batched API calls to improve performance by reducing the number of requests
 * @param projects - Array of projects to analyze
 * @param selectedFileTypes - Selected file type filters
 * @returns Object with filtered projects and loading state
 */
export function useProjectsWithFileTypes(
  projects: GetAdminProjectResponse[],
  selectedFileTypes: string[],
) {
  // Create batches of project IDs
  const projectBatches = useMemo(() => {
    const projectIds = projects.map((p) => p._id);
    return chunkArray(projectIds, BATCH_SIZE);
  }, [projects]);

  // Fetch files for all project batches using useQueries
  const batchQueries = useQueries({
    queries: projectBatches.map((batch) => ({
      queryKey: createBatchQueryKey(batch),
      queryFn: () => getMultipleProjectFiles2(batch),
      enabled: batch.length > 0,
      staleTime: STALE_TIME,
    })),
  });

  // Check if any queries are still loading
  const isLoading = batchQueries.some((query) => query.isLoading);

  // Create a consolidated map of project files from all batch responses
  const projectFilesMap = useMemo(() => {
    const consolidatedMap = new Map<string, SitelifeFile[]>();

    batchQueries.forEach((query) => {
      if (query.data) {
        const batchMap = createProjectFilesMap(query.data);
        batchMap.forEach((files, projectId) => {
          consolidatedMap.set(projectId, files);
        });
      }
    });

    return consolidatedMap;
  }, [batchQueries]);

  // Apply file type filtering
  const filteredProjects = useMemo(() => {
    if (selectedFileTypes.length === 0) {
      return projects;
    }

    return projects.filter((project) => {
      const files = projectFilesMap.get(project._id);
      return projectMatchesFileTypeFilter(project, files, selectedFileTypes);
    });
  }, [projects, projectFilesMap, selectedFileTypes]);

  return {
    projects: filteredProjects,
    isLoading,
    // Provide the raw data for individual row rendering - maintains backward compatibility
    getProjectFiles: (projectId: string) => {
      return projectFilesMap.get(projectId);
    },
  };
}
